import { NavLink, Outlet, useNavigate } from 'react-router-dom'

import { Button } from '../components/ui'
import { t } from '../i18n'
import type { MessageKey } from '../i18n'
import type { Permission } from '../api/permissions'
import { useSession } from '../auth/session'
import { SIGN_IN_PATH } from '../auth/guards'

interface NavEntry {
  readonly to: string
  readonly labelKey: MessageKey
  /**
   * The permission that makes this section worth showing. A person without it
   * would reach a screen the server refuses to fill, so the link is left out —
   * cosmetic, like every client-side permission check here.
   */
  readonly permission: Permission
}

const SECTIONS: ReadonlyArray<{
  readonly headingKey: MessageKey
  readonly entries: readonly NavEntry[]
}> = [
  {
    headingKey: 'nav.sectionMain',
    entries: [
      { to: '/inbox', labelKey: 'nav.inbox', permission: 'threads.read' },
      { to: '/objects', labelKey: 'nav.objects', permission: 'listings.read' },
      { to: '/catalogue', labelKey: 'nav.catalogue', permission: 'listings.read' },
    ],
  },
  {
    headingKey: 'nav.sectionSystem',
    entries: [{ to: '/settings', labelKey: 'nav.settings', permission: 'settings.read' }],
  },
]

function navClass({ isActive }: { isActive: boolean }): string {
  return isActive ? 'nav__link nav__link--active' : 'nav__link'
}

export function AppShell() {
  const { user, can, signOut, isSigningOut } = useSession()
  const navigate = useNavigate()

  async function handleSignOut() {
    await signOut()
    navigate(SIGN_IN_PATH, { replace: true })
  }

  const sections = SECTIONS.map((section) => ({
    ...section,
    entries: section.entries.filter((entry) => can(entry.permission)),
  })).filter((section) => section.entries.length > 0)

  return (
    <div className="shell">
      <aside className="sidebar">
        <div className="sidebar__brand">
          <span className="sidebar__brand-name">{t('app.name')}</span>
          <span className="sidebar__brand-tagline">{t('app.tagline')}</span>
        </div>

        <nav className="nav" aria-label={t('app.tagline')}>
          {sections.map((section) => (
            <div key={section.headingKey}>
              <div className="nav__heading">{t(section.headingKey)}</div>
              {section.entries.map((entry) => (
                <NavLink key={entry.to} to={entry.to} className={navClass}>
                  {t(entry.labelKey)}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        <div className="sidebar__spacer" />

        <div className="account">
          <div className="account__identity">
            {/*
              The API returns id, email, role and permissions — there is no name
              field, so the email is the honest thing to show. If a display name
              is wanted, it has to come from the server first.
            */}
            <span className="account__email">{user?.email}</span>
            <span className="account__meta">
              {t('session.role')}: {user?.role} · {t('session.permissionCount', {
                count: user?.permissions.length ?? 0,
              })}
            </span>
          </div>
          <Button
            variant="secondary"
            block
            busy={isSigningOut}
            onClick={() => {
              void handleSignOut()
            }}
          >
            {isSigningOut ? t('session.signingOut') : t('session.signOut')}
          </Button>
        </div>
      </aside>

      <main className="content">
        <Outlet />
      </main>
    </div>
  )
}
